import { useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, Chip, Tab, Tabs, ThemeProvider } from '@mui/material';

import DateRangeSelector from 'components/v2/common/dateRangeSelector';
import PageHeader from 'components/v2/common/pageHeader';
import AdminReportDownload from './components/AdminReportDownload';
import ManagementReportDownload from './components/ManagementReportDownload';

import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import type { AnalyticsDateRangeKey } from './hooks/useAnalyticsFilters';
import { useIsOwnerRole } from './hooks/useIsOwnerRole';
import useAnalyticsFilters from './hooks/useAnalyticsFilters';
import appThemeV2 from 'theme/themeV2';

interface TabRoute {
    labelKey: string;
    path: string;
    systemAdminOnly?: boolean;
}

const TAB_ROUTES: TabRoute[] = [
    { labelKey: 'analyticsToday', path: 'today' },
    { labelKey: 'analyticsConversion', path: 'conversion' },
    { labelKey: 'analyticsRevenue', path: 'revenue' },
    { labelKey: 'analyticsOperations', path: 'operations' },
    { labelKey: 'summaryProviderActivity', path: 'overview', systemAdminOnly: true },
];

export interface AnalyticsOutletContext {
    isOwner: boolean;
    isSuperSuperAdmin: boolean;
    fromDate: Date | null;
    toDate: Date | null;
    hasValidDateRange: boolean;
    from?: string;
    to?: string;
    serviceProviderId?: string;
    serviceProviderIdRaw: string;
    setServiceProviderId: (id: string) => void;
    dateRangeKey: AnalyticsDateRangeKey;
}

const AnalyticsShell = ({ admin = false }: { admin?: boolean }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isOwner } = useIsOwnerRole();
    const { role } = useAuth();
    const { translate } = useTranslate();
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const filters = useAnalyticsFilters();

    const visibleTabs = useMemo(
        () => TAB_ROUTES.filter((tab) => Boolean(tab.systemAdminOnly) === admin),
        [admin]
    );

    const currentTab = useMemo(() => {
        const path = location.pathname.split('/').pop();
        const index = visibleTabs.findIndex((t) => t.path === path);
        return index >= 0 ? index : 0;
    }, [location.pathname, visibleTabs]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        navigate({
            pathname: visibleTabs[newValue].path,
            search: location.search,
        });
    };

    const outletContext: AnalyticsOutletContext = {
        isOwner,
        isSuperSuperAdmin,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        hasValidDateRange: filters.hasValidDateRange,
        from: filters.from,
        to: filters.to,
        serviceProviderId: filters.serviceProviderIdScope,
        serviceProviderIdRaw: filters.serviceProviderId,
        setServiceProviderId: filters.setServiceProviderId,
        dateRangeKey: filters.dateRangeKey,
    };

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box sx={{ width: '100%' }}>
                <PageHeader
                    title={admin ? 'adminAnalytics' : 'analytics'}
                    // description={admin ? 'adminAnalyticsDescription' : undefined}
                    sx={{ mb: 2 }}
                >
                    {isSuperSuperAdmin && !admin ? (
                        <Box
                            sx={{
                                width: { xs: '100% !important', sm: '220px !important' },
                                minWidth: 0,
                            }}
                        >
                            <AdminReportDownload
                                fromDate={filters.fromDate}
                                toDate={filters.toDate}
                                serviceProviderId={filters.serviceProviderId}
                                hasValidDateRange={filters.hasValidDateRange}
                                onServiceProviderIdChange={filters.setServiceProviderId}
                                showDownloadButton={false}
                            />
                        </Box>
                    ) : null}
                </PageHeader>

                <Box sx={{ px: { xs: 1, sm: 2 }, pb: 2, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: { xs: 'nowrap', sm: 'wrap' },
                            alignItems: 'start',
                            justifyContent: 'flex-end',
                            gap: 1,
                            mb: 2,
                            minWidth: 0,
                        }}
                    >
                        <DateRangeSelector
                            rangeKey={filters.dateRangeKey}
                            presets={['7', '30', '90']}
                            fromDate={filters.fromDate}
                            toDate={filters.toDate}
                            hasValidDateRange={filters.hasValidDateRange}
                            onRangeChange={filters.setDateRangeKey}
                            onFromDateChange={filters.setFromDate}
                            onToDateChange={filters.setToDate}
                        />
                        {admin ? (
                            <Box sx={{ width: { xs: '100%', sm: 500 }, flexShrink: 0 }}>
                                <AdminReportDownload
                                    fromDate={filters.fromDate}
                                    toDate={filters.toDate}
                                    serviceProviderId={filters.serviceProviderId}
                                    hasValidDateRange={filters.hasValidDateRange}
                                    onServiceProviderIdChange={filters.setServiceProviderId}
                                />
                            </Box>
                        ) : (
                            <Box sx={{ width: { xs: '100%', sm: 230 }, flexShrink: 0 }}>
                                <ManagementReportDownload
                                    from={filters.from}
                                    to={filters.to}
                                    hasValidDateRange={filters.hasValidDateRange}
                                />
                            </Box>
                        )}
                    </Box>

                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                        sx={{
                            mb: 0,
                            minHeight: 44,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontSize: 14,
                                fontWeight: 600,
                                color: 'text.secondary',
                                minWidth: 'auto',
                                minHeight: 44,
                                px: { xs: 1.5, sm: 2 },
                                py: 1,
                                '&:hover': { color: 'text.primary' },
                                '&.Mui-selected': { color: 'primary.main' },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'primary.main',
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                            },
                        }}
                    >
                        {visibleTabs.map((tab) => (
                            <Tab
                                key={tab.path}
                                label={
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                        }}
                                    >
                                        {translate(tab.labelKey)}
                                        {tab.systemAdminOnly ? (
                                            <Chip
                                                label={translate('systemAdmin')}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    height: 20,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.02em',
                                                }}
                                            />
                                        ) : null}
                                    </Box>
                                }
                                disableRipple
                            />
                        ))}
                    </Tabs>
                </Box>

                <Outlet context={outletContext} />
            </Box>
        </ThemeProvider>
    );
};

export default AnalyticsShell;
